import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  Card,
  CardContent,
  CardActions,
  Divider,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Autocomplete,
  Avatar,
  Switch,
  FormControlLabel,
  LinearProgress,
  Checkbox,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  HourglassBottom as HourglassIcon,
  Work as WorkIcon,
  BrokenImage as BrokenImageIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  RadioButtonUnchecked as UncheckedIcon,
  QrCode2 as BarcodeIcon,
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  AddShoppingCart as GeneratePOIcon,
} from '@mui/icons-material';
import { usePermissions } from '../hooks/usePermissions';
import dupattaDesignsApi, { DupattaDesignRecord, DupattaDesignStatus } from '../api/dupattaDesigns';
import { getDupattaTypes } from '../services/api';
import instance from '../utils/axios';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DupattaType {
  id: number;
  name: string;
  isActive: boolean;
}

interface DesignOption {
  design_no: string;
  name?: string | null;
  product_type?: string | null;
  image_url?: string | null;
  approval_status: string;
}

interface FormState {
  designNo: string;
  dupattaTypeId: string;
  description: string;
  quantity: string;
  dupattaImageUrl: string;
}

const emptyForm: FormState = {
  designNo: '',
  dupattaTypeId: '',
  description: '',
  quantity: '',
  dupattaImageUrl: '',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const AgingChip: React.FC<{ days: number }> = ({ days }) => (
  <Chip
    icon={<HourglassIcon />}
    label={`${days}d`}
    color={days > 30 ? 'error' : days > 14 ? 'warning' : 'success'}
    size="small"
    variant="outlined"
  />
);

const JobWorkBadge: React.FC<{ jw: DupattaDesignRecord['jobWorks'][0] }> = ({ jw }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', p: 0.75, borderRadius: 1, bgcolor: 'action.hover', mb: 0.5, overflow: 'hidden' }}>
    <Chip label={jw.vendorName} size="small" color="primary" variant="filled" sx={{ maxWidth: '40%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />
    <Chip label={jw.jobType} size="small" variant="outlined" />
    <AgingChip days={jw.agingDays} />
    <Chip label={`Qty: ${jw.qty}`} size="small" color="info" variant="outlined" />
  </Box>
);

/** Clickable image thumbnail — opens lightbox on click.
 *  width / height can differ for a portrait aspect ratio (e.g. w=52 h=68). */
const ThumbImage: React.FC<{
  url?: string | null;
  alt?: string;
  width?: number;
  height?: number;
  onZoom: (url: string) => void;
}> = ({ url, alt = '', width = 52, height = 68, onZoom }) => {
  const [broken, setBroken] = useState(false);

  if (!url || broken) {
    return (
      <Avatar variant="rounded" sx={{ width, height, bgcolor: 'grey.100', flexShrink: 0 }}>
        <BrokenImageIcon sx={{ fontSize: Math.round(Math.min(width, height) * 0.55), color: 'grey.400' }} />
      </Avatar>
    );
  }

  return (
    <Tooltip title="Click to enlarge" placement="right">
      <Box
        component="img"
        src={url}
        alt={alt}
        onError={() => setBroken(true)}
        onClick={() => onZoom(url)}
        sx={{
          width, height, objectFit: 'cover', borderRadius: 1, flexShrink: 0,
          cursor: 'zoom-in',
          transition: 'opacity 0.15s',
          '&:hover': { opacity: 0.82 },
        }}
      />
    </Tooltip>
  );
};

/** Two thumbnails side by side: design image (left) + dupatta image (right) */
const DesignDupattaThumbs: React.FC<{
  designImageUrl?: string | null;
  dupattaImageUrl?: string | null;
  thumbWidth?: number;
  thumbHeight?: number;
  onZoom: (url: string) => void;
}> = ({ designImageUrl, dupattaImageUrl, thumbWidth = 52, thumbHeight = 68, onZoom }) => (
  <Stack direction="row" spacing={0.5} alignItems="center">
    <ThumbImage url={designImageUrl} alt="Design" width={thumbWidth} height={thumbHeight} onZoom={onZoom} />
    {dupattaImageUrl && (
      <>
        <Box sx={{ width: '1px', height: thumbHeight * 0.6, bgcolor: 'divider' }} />
        <Tooltip title="Dupatta image" placement="right">
          <Box sx={{ position: 'relative' }}>
            <ThumbImage url={dupattaImageUrl} alt="Dupatta" width={thumbWidth} height={thumbHeight} onZoom={onZoom} />
          </Box>
        </Tooltip>
      </>
    )}
  </Stack>
);

// ─── Fetch helpers ────────────────────────────────────────────────────────────

const fetchApprovedDesigns = async (q = ''): Promise<DesignOption[]> => {
  const pageSize = 200;
  let page = 1;
  let all: DesignOption[] = [];
  while (true) {
    const resp = await instance.get('/designs', {
      params: { summary: 1, page, pageSize, withCount: 1, q: q || undefined },
    });
    const data: DesignOption[] = Array.isArray(resp.data) ? resp.data : [];
    all = all.concat(data.filter((d) => d.approval_status === 'APPROVED'));
    const total = Number(resp.headers['x-total-count'] || 0);
    if (total > 0 ? page * pageSize >= total : data.length < pageSize) break;
    page += 1;
  }
  return all;
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const DupattaTrackerPage: React.FC = () => {
  const { canAdd, canEdit, canDelete } = usePermissions();
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  // listing
  const [records, setRecords] = useState<DupattaDesignRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [search, setSearch] = useState(() => localStorage.getItem('dupattaTracker_search') || '');
  const [showCompleted, setShowCompleted] = useState(false);
  const [dupattaTypes, setDupattaTypes] = useState<DupattaType[]>([]);

  // bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // lightbox
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  // dialog / form
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<DupattaDesignRecord | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // image upload state
  const [uploadingDupatta, setUploadingDupatta] = useState(false);
  const dupattaFileRef = useRef<HTMLInputElement>(null);

  // design autocomplete
  const [designOptions, setDesignOptions] = useState<DesignOption[]>([]);
  const [designsLoading, setDesignsLoading] = useState(false);
  const [designInputValue, setDesignInputValue] = useState('');
  const [selectedDesign, setSelectedDesign] = useState<DesignOption | null>(null);
  const designSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Persist ────────────────────────────────────────────────────────────────
  useEffect(() => { localStorage.setItem('dupattaTracker_search', search); }, [search]);

  // ── Fetch listing ──────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [data, typesRes] = await Promise.all([
        dupattaDesignsApi.getAll({ search: search || undefined, status: showCompleted ? 'ALL' : 'ACTIVE' }),
        getDupattaTypes(),
      ]);
      setRecords(data);
      setDupattaTypes(typesRes.data.filter((t: DupattaType) => t.isActive));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [search, showCompleted]);

  useEffect(() => {
    const timer = setTimeout(fetchData, 300);
    return () => clearTimeout(timer);
  }, [fetchData]);

  // ── Bulk selection ─────────────────────────────────────────────────────────
  const handleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(records.map(r => r.id)) : new Set());
  };
  const handleSelectOne = (id: number, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      checked ? next.add(id) : next.delete(id);
      return next;
    });
  };
  const handleRaiseBulkPO = () => {
    const designNos = records
      .filter(r => selectedIds.has(r.id))
      .map(r => r.designNo);
    navigate(`/purchase-orders?new=1&design_nos=${encodeURIComponent(designNos.join(','))}&garment_part=DUPATTA`);
  };

  // ── Load design options ────────────────────────────────────────────────────
  useEffect(() => {
    if (dialogOpen && !editingRecord) loadDesignOptions('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen]);

  const loadDesignOptions = async (q: string) => {
    setDesignsLoading(true);
    try { setDesignOptions(await fetchApprovedDesigns(q)); } catch { /* silent */ }
    finally { setDesignsLoading(false); }
  };

  // ── Dialog helpers ─────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditingRecord(null);
    setForm(emptyForm);
    setFormError(null);
    setSelectedDesign(null);
    setDesignInputValue('');
    setDesignOptions([]);
    setDialogOpen(true);
  };

  const openEdit = (record: DupattaDesignRecord) => {
    setEditingRecord(record);
    setForm({
      designNo: record.designNo,
      dupattaTypeId: record.dupattaType ? String(record.dupattaType.id) : '',
      description: record.description || '',
      quantity: String(record.quantity),
      dupattaImageUrl: record.dupattaImageUrl || '',
    });
    setSelectedDesign(record.design ? {
      design_no: record.design.design_no,
      name: record.design.name,
      product_type: record.design.product_type,
      image_url: record.design.image_url,
      approval_status: record.design.approval_status,
    } : null);
    setDesignInputValue(record.designNo);
    setFormError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingRecord(null);
    setForm(emptyForm);
    setFormError(null);
    setSelectedDesign(null);
    setDesignInputValue('');
  };

  // ── Dupatta image upload ───────────────────────────────────────────────────
  const handleDupattaImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setFormError('Only image files are allowed');
      return;
    }
    try {
      setUploadingDupatta(true);
      const formData = new FormData();
      formData.append('file', file);
      const res = await instance.post('/upload?module=dupatta-images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({ ...f, dupattaImageUrl: res.data.url }));
    } catch {
      setFormError('Failed to upload dupatta image. Please try again.');
    } finally {
      setUploadingDupatta(false);
      if (dupattaFileRef.current) dupattaFileRef.current.value = '';
    }
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setFormError(null);
    if (!form.designNo.trim()) { setFormError('Please select a design'); return; }
    try {
      setSaving(true);
      const payload = {
        dupattaTypeId: form.dupattaTypeId ? parseInt(form.dupattaTypeId, 10) : null,
        description: form.description || undefined,
        quantity: form.quantity ? parseInt(form.quantity, 10) : 0,
        dupattaImageUrl: form.dupattaImageUrl || null,
      };
      if (editingRecord) {
        await dupattaDesignsApi.update(editingRecord.id, payload);
        setSuccess('Record updated');
      } else {
        await dupattaDesignsApi.create({ designNo: form.designNo.trim(), ...payload });
        setSuccess('Record added');
      }
      closeDialog();
      fetchData();
    } catch (err: any) {
      setFormError(err.response?.data?.error || 'Failed to save record');
    } finally {
      setSaving(false);
    }
  };

  // ── Status toggle ──────────────────────────────────────────────────────────
  const handleToggleStatus = async (record: DupattaDesignRecord) => {
    const newStatus: DupattaDesignStatus = record.status === 'ACTIVE' ? 'COMPLETED' : 'ACTIVE';
    try {
      await dupattaDesignsApi.setStatus(record.id, newStatus);
      setSuccess(`Marked as ${newStatus === 'COMPLETED' ? 'Completed' : 'Active'}`);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update status');
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (record: DupattaDesignRecord) => {
    if (!window.confirm(`Delete dupatta record for design "${record.designNo}"?`)) return;
    try {
      await dupattaDesignsApi.delete(record.id);
      setSuccess('Record deleted');
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete record');
    }
  };

  // ── Status chip ────────────────────────────────────────────────────────────
  const StatusIndicator: React.FC<{ record: DupattaDesignRecord }> = ({ record }) => {
    if (record.status === 'COMPLETED') {
      return <Chip icon={<CheckCircleOutlineIcon />} label="Completed" size="small" color="success" variant="filled" />;
    }
    if (record.barcodedBatch) {
      return (
        <Tooltip title={`Batch ${record.barcodedBatch.batchNumber}: ${record.barcodedBatch.totalBarcoded} barcoded`} arrow>
          <Chip icon={<BarcodeIcon />} label={`Barcoded ${record.barcodedBatch.totalBarcoded}`} size="small" color="info" variant="filled" />
        </Tooltip>
      );
    }
    return <Chip icon={<UncheckedIcon />} label="Active" size="small" color="default" variant="outlined" />;
  };

  // ── Mobile card ────────────────────────────────────────────────────────────
  const MobileCard: React.FC<{ record: DupattaDesignRecord }> = ({ record }) => (
    <Card
      variant="outlined"
      sx={{
        mb: 1.5,
        overflow: 'hidden',
        opacity: record.status === 'COMPLETED' ? 0.72 : 1,
        ...(selectedIds.has(record.id) && { borderColor: 'primary.main', borderWidth: 2 }),
      }}
    >
      <CardContent sx={{ pb: 1, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1, alignItems: 'flex-start' }}>
          <DesignDupattaThumbs
            designImageUrl={record.design?.image_url}
            dupattaImageUrl={record.dupattaImageUrl}
            thumbWidth={52}
            thumbHeight={68}
            onZoom={setLightboxUrl}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>{record.designNo}</Typography>
                {record.design?.name && <Typography variant="caption" color="text.secondary">{record.design.name}</Typography>}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                {record.dupattaType && <Chip label={record.dupattaType.name} size="small" color="secondary" variant="outlined" />}
                <Checkbox
                  size="small"
                  checked={selectedIds.has(record.id)}
                  onChange={e => handleSelectOne(record.id, e.target.checked)}
                  onClick={e => e.stopPropagation()}
                  sx={{ p: 0.25 }}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {record.description && <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{record.description}</Typography>}

        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
          <Chip
            label={record.topCutQty != null ? `Qty: ${record.quantity} / ${record.topCutQty}` : `Qty: ${record.quantity}`}
            size="small"
            variant="outlined"
          />
          {record.design?.product_type && <Chip label={record.design.product_type} size="small" variant="outlined" />}
          <StatusIndicator record={record} />
        </Stack>

        <Divider sx={{ my: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
          DUPATTA JOB WORK
        </Typography>
        {record.jobWorks.length > 0
          ? record.jobWorks.map((jw) => <JobWorkBadge key={jw.id} jw={jw} />)
          : <Chip label="Not Issued" size="small" variant="outlined" color="default" icon={<WorkIcon />} />}

        {record.purchaseOrders && record.purchaseOrders.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              PURCHASE ORDERS
            </Typography>
            <Stack spacing={0.5}>
              {record.purchaseOrders.map((po) => (
                <Box
                  key={po.id}
                  sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', p: 0.5, borderRadius: 1, bgcolor: 'action.hover', cursor: 'pointer', overflow: 'hidden' }}
                  onClick={() => navigate(`/purchase-orders?po=${po.po_number}`)}
                >
                  <Chip label={po.po_number} size="small" color="secondary" variant="outlined" sx={{ fontWeight: 700, fontSize: '0.7rem' }} />
                  <Chip label={po.vendor} size="small" color="primary" sx={{ maxWidth: '35%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />
                  <Chip label={`Qty ${po.totalQty}`} size="small" color="info" variant="outlined" />
                  {po.garmentParts.map(gp => (
                    <Chip key={gp} label={gp} size="small" color={gp === 'DUPATTA' ? 'secondary' : 'default'} variant="filled" sx={{ fontSize: '0.65rem', height: 18 }} />
                  ))}
                </Box>
              ))}
            </Stack>
          </>
        )}
      </CardContent>
      <CardActions sx={{ pt: 0, justifyContent: 'flex-end', gap: 0.5 }}>
        {canAdd('purchase-orders') && (
          <Tooltip title="Generate Purchase Order for this design">
            <IconButton size="small" color="primary" onClick={() => navigate(`/purchase-orders?new=1&design_no=${record.designNo}&garment_part=DUPATTA`)}>
              <GeneratePOIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {canEdit('dupatta-tracker') && (
          <Tooltip title={record.status === 'COMPLETED' ? 'Mark Active' : 'Mark Completed'}>
            <IconButton size="small" color={record.status === 'COMPLETED' ? 'default' : 'success'} onClick={() => handleToggleStatus(record)}>
              {record.status === 'COMPLETED' ? <UncheckedIcon fontSize="small" /> : <CheckCircleOutlineIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        {canEdit('dupatta-tracker') && (
          <IconButton size="small" onClick={() => openEdit(record)}><EditIcon fontSize="small" /></IconButton>
        )}
        {canDelete('dupatta-tracker') && (
          <IconButton size="small" color="error" onClick={() => handleDelete(record)}><DeleteIcon fontSize="small" /></IconButton>
        )}
      </CardActions>
    </Card>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700}>Dupatta Tracker</Typography>
          <Typography variant="body2" color="text.secondary">Track approved designs &amp; dupatta job works</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <FormControlLabel
            control={<Switch size="small" checked={showCompleted} onChange={(e) => setShowCompleted(e.target.checked)} />}
            label={<Typography variant="caption">Show Completed</Typography>}
          />
          {canAdd('dupatta-tracker') && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} size={isMobile ? 'small' : 'medium'}>
              Add Design
            </Button>
          )}
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Search */}
      <TextField
        fullWidth size="small"
        placeholder="Search by design no, name or dupatta type…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
        }}
      />

      {/* Listing */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : records.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {search ? 'No records match your search.' : 'No dupatta records yet. Click "Add Design" to get started.'}
          </Typography>
        </Paper>
      ) : isMobile ? (
        <Box>
          {/* Mobile bulk action bar */}
          {selectedIds.size > 0 && (
            <Box sx={{ px: 2, py: 1.5, mb: 1, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200', flexWrap: 'wrap' }}>
              <Typography variant="body2" fontWeight={600} color="primary.main">
                {selectedIds.size} selected
              </Typography>
              {canAdd('purchase-orders') && (
                <Button variant="outlined" size="small" color="success" startIcon={<GeneratePOIcon />} onClick={handleRaiseBulkPO}>
                  Raise PO ({selectedIds.size})
                </Button>
              )}
              <Button size="small" onClick={() => setSelectedIds(new Set())}>Clear</Button>
            </Box>
          )}
          {/* Select all for mobile */}
          {records.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5, mb: 0.5 }}>
              <Checkbox
                size="small"
                indeterminate={selectedIds.size > 0 && selectedIds.size < records.length}
                checked={records.length > 0 && selectedIds.size === records.length}
                onChange={e => handleSelectAll(e.target.checked)}
              />
              <Typography variant="caption" color="text.secondary">Select all</Typography>
            </Box>
          )}
          {records.map((r) => <MobileCard key={r.id} record={r} />)}
        </Box>
      ) : (
        <Paper variant="outlined" sx={{ width: '100%', overflow: 'hidden' }}>
          {/* Bulk action bar */}
          {selectedIds.size > 0 && (
            <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 2, bgcolor: 'primary.50', borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
              <Typography variant="body2" fontWeight={600} color="primary.main">
                {selectedIds.size} selected
              </Typography>
              {canAdd('purchase-orders') && (
                <Button
                  variant="outlined"
                  size="small"
                  color="success"
                  startIcon={<GeneratePOIcon />}
                  onClick={handleRaiseBulkPO}
                >
                  Raise PO ({selectedIds.size})
                </Button>
              )}
              <Button size="small" onClick={() => setSelectedIds(new Set())}>
                Clear Selection
              </Button>
            </Box>
          )}
          <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
          <Table size="small" sx={{ minWidth: 1300 }}>
            <TableHead>
              <TableRow>
                {/* ── Sticky columns ── */}
                <TableCell padding="checkbox" sx={{ fontWeight: 700, width: 40, position: 'sticky', left: 0, zIndex: 3, bgcolor: 'grey.50' }}>
                  <Checkbox
                    size="small"
                    indeterminate={selectedIds.size > 0 && selectedIds.size < records.length}
                    checked={records.length > 0 && selectedIds.size === records.length}
                    onChange={e => handleSelectAll(e.target.checked)}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 700, width: 112, position: 'sticky', left: 40, zIndex: 3, bgcolor: 'grey.50' }}>Images</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 110, position: 'sticky', left: 152, zIndex: 3, bgcolor: 'grey.50', borderRight: '2px solid', borderColor: 'divider' }}>Design</TableCell>
                {/* ── Scrollable columns ── */}
                <TableCell sx={{ fontWeight: 700, width: 110 }}>Dupatta Type</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 150 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 70 }} align="center">
                  <Tooltip title="Entered qty / TOP cut qty" arrow><span>Qty</span></Tooltip>
                </TableCell>
                <TableCell sx={{ fontWeight: 700, width: 100 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 240 }}>Job Work</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 220 }}>Purchase Orders</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 130 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((record, index) => (
                <TableRow
                  key={record.id}
                  hover
                  selected={selectedIds.has(record.id)}
                  sx={{ opacity: record.status === 'COMPLETED' ? 0.65 : 1 }}
                >
                  {/* ── Sticky: Checkbox ── */}
                  <TableCell padding="checkbox" onClick={e => e.stopPropagation()} sx={{ position: 'sticky', left: 0, zIndex: 1, bgcolor: 'background.paper' }}>
                    <Checkbox
                      size="small"
                      checked={selectedIds.has(record.id)}
                      onChange={e => handleSelectOne(record.id, e.target.checked)}
                    />
                  </TableCell>
                  {/* ── Sticky: Images ── */}
                  <TableCell sx={{ py: 0.5, position: 'sticky', left: 40, zIndex: 1, bgcolor: 'background.paper' }}>
                    <DesignDupattaThumbs
                      designImageUrl={record.design?.image_url}
                      dupattaImageUrl={record.dupattaImageUrl}
                      thumbWidth={48}
                      thumbHeight={62}
                      onZoom={setLightboxUrl}
                    />
                  </TableCell>
                  {/* ── Sticky: Design No + Name ── */}
                  <TableCell sx={{ position: 'sticky', left: 152, zIndex: 1, bgcolor: 'background.paper', borderRight: '2px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" fontWeight={600}>{record.designNo}</Typography>
                    {record.design?.name && (
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 100 }}>{record.design.name}</Typography>
                    )}
                    {record.design?.product_type && (
                      <Typography variant="caption" color="text.disabled">{record.design.product_type}</Typography>
                    )}
                  </TableCell>
                  {/* ── Scrollable columns ── */}
                  <TableCell>
                    {record.dupattaType
                      ? <Chip label={record.dupattaType.name} size="small" color="secondary" variant="outlined" />
                      : <Typography variant="body2" color="text.secondary">—</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {record.description || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.25 }}>
                      <Typography variant="body2" fontWeight={600}>{record.quantity}</Typography>
                      {record.topCutQty != null && (
                        <>
                          <Typography variant="caption" color="text.disabled">/</Typography>
                          <Typography variant="body2" color="text.secondary">{record.topCutQty}</Typography>
                        </>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell><StatusIndicator record={record} /></TableCell>
                  {/* Job Work */}
                  <TableCell>
                    {record.jobWorks.length > 0 ? (
                      <Stack spacing={0.5}>
                        {record.jobWorks.map((jw) => (
                          <Tooltip key={jw.id} title={`Status: ${jw.status} | Code: ${jw.jobWorkCode}`} arrow>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', p: 0.5, borderRadius: 1, bgcolor: 'action.hover', cursor: 'default' }}>
                              <Chip label={jw.vendorName} size="small" color="primary" sx={{ maxWidth: 120, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />
                              <Chip label={jw.jobType} size="small" variant="outlined" />
                              <AgingChip days={jw.agingDays} />
                              <Chip label={`Qty ${jw.qty}`} size="small" color="info" variant="outlined" />
                            </Box>
                          </Tooltip>
                        ))}
                      </Stack>
                    ) : (
                      <Chip label="Not Issued" size="small" variant="outlined" color="default" icon={<WorkIcon />} />
                    )}
                  </TableCell>
                  {/* Purchase Orders */}
                  <TableCell>
                    {record.purchaseOrders && record.purchaseOrders.length > 0 ? (
                      <Stack spacing={0.5}>
                        {record.purchaseOrders.map((po) => (
                          <Tooltip key={po.id} title={`PO: ${po.po_number} | Status: ${po.status}`} arrow>
                            <Box
                              sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', p: 0.5, borderRadius: 1, bgcolor: 'action.hover', cursor: 'pointer' }}
                              onClick={() => navigate(`/purchase-orders?po=${po.po_number}`)}
                            >
                              <Chip label={po.po_number} size="small" color="secondary" variant="outlined" sx={{ fontWeight: 700, fontSize: '0.7rem' }} />
                              <Chip label={po.vendor} size="small" color="primary" sx={{ maxWidth: 100, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />
                              <Chip label={`Qty ${po.totalQty}`} size="small" color="info" variant="outlined" />
                              {po.garmentParts.map(gp => (
                                <Chip key={gp} label={gp} size="small" color={gp === 'DUPATTA' ? 'secondary' : 'default'} variant="filled" sx={{ fontSize: '0.65rem', height: 18 }} />
                              ))}
                            </Box>
                          </Tooltip>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.disabled">No PO</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" justifyContent="flex-end" spacing={0.25}>
                      {canAdd('purchase-orders') && (
                        <Tooltip title="Generate Purchase Order for this design">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/purchase-orders?new=1&design_no=${record.designNo}&garment_part=DUPATTA`)}
                          >
                            <GeneratePOIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canEdit('dupatta-tracker') && (
                        <Tooltip title={record.status === 'COMPLETED' ? 'Reopen (Mark Active)' : 'Mark Completed'}>
                          <IconButton
                            size="small"
                            color={record.status === 'COMPLETED' ? 'default' : 'success'}
                            onClick={() => handleToggleStatus(record)}
                          >
                            {record.status === 'COMPLETED'
                              ? <UncheckedIcon fontSize="small" />
                              : <CheckCircleOutlineIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      )}
                      {canEdit('dupatta-tracker') && (
                        <IconButton size="small" onClick={() => openEdit(record)}><EditIcon fontSize="small" /></IconButton>
                      )}
                      {canDelete('dupatta-tracker') && (
                        <IconButton size="small" color="error" onClick={() => handleDelete(record)}><DeleteIcon fontSize="small" /></IconButton>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ── Lightbox ──────────────────────────────────────────────────────── */}
      <Dialog
        open={!!lightboxUrl}
        onClose={() => setLightboxUrl(null)}
        maxWidth="lg"
        PaperProps={{ sx: { bgcolor: 'black', p: 0, m: 1 } }}
      >
        <Box sx={{ position: 'relative' }}>
          <IconButton
            onClick={() => setLightboxUrl(null)}
            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, bgcolor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' } }}
          >
            <CloseIcon />
          </IconButton>
          {lightboxUrl && (
            <Box
              component="img"
              src={lightboxUrl}
              alt="Preview"
              sx={{ display: 'block', maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }}
            />
          )}
        </Box>
      </Dialog>

      {/* ── Add / Edit Dialog ─────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{editingRecord ? `Edit: ${editingRecord.designNo}` : 'Add Dupatta Design'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {formError && <Alert severity="error" onClose={() => setFormError(null)}>{formError}</Alert>}

            {/* Design autocomplete (add only) */}
            {!editingRecord ? (
              <>
                <Autocomplete
                  options={designOptions}
                  loading={designsLoading}
                  value={selectedDesign}
                  inputValue={designInputValue}
                  getOptionLabel={(opt) => opt.design_no}
                  isOptionEqualToValue={(opt, val) => opt.design_no === val.design_no}
                  filterOptions={(opts) => opts}
                  onInputChange={(_e, val, reason) => {
                    setDesignInputValue(val);
                    if (reason === 'input') {
                      if (designSearchTimer.current) clearTimeout(designSearchTimer.current);
                      designSearchTimer.current = setTimeout(() => loadDesignOptions(val), 350);
                    }
                  }}
                  onChange={(_e, newVal) => {
                    setSelectedDesign(newVal);
                    setForm((f) => ({ ...f, designNo: newVal ? newVal.design_no : '' }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Design No *"
                      helperText="Only APPROVED designs are listed"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {designsLoading && <CircularProgress size={16} sx={{ mr: 1 }} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.design_no}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: '6px !important' }}>
                      {option.image_url ? (
                        <Box component="img" src={option.image_url} alt={option.design_no}
                          sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                          onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : (
                        <Avatar variant="rounded" sx={{ width: 40, height: 40, bgcolor: 'grey.100', flexShrink: 0 }}>
                          <BrokenImageIcon sx={{ fontSize: 20, color: 'grey.400' }} />
                        </Avatar>
                      )}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>{option.design_no}</Typography>
                        {option.name && <Typography variant="caption" color="text.secondary" noWrap>{option.name}</Typography>}
                        {option.product_type && <Typography variant="caption" color="text.disabled" display="block" noWrap>{option.product_type}</Typography>}
                      </Box>
                    </Box>
                  )}
                />

                {/* Selected design preview */}
                {selectedDesign?.image_url && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
                    <Box
                      component="img"
                      src={selectedDesign.image_url}
                      alt={selectedDesign.design_no}
                      onClick={() => selectedDesign.image_url && setLightboxUrl(selectedDesign.image_url)}
                      sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1.5, flexShrink: 0, cursor: 'zoom-in' }}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight={700}>{selectedDesign.design_no}</Typography>
                      {selectedDesign.name && <Typography variant="caption" color="text.secondary">{selectedDesign.name}</Typography>}
                      {selectedDesign.product_type && <Typography variant="caption" color="text.disabled" display="block">{selectedDesign.product_type}</Typography>}
                      <Chip label="APPROVED" size="small" color="success" variant="filled" sx={{ mt: 0.5 }} />
                    </Box>
                  </Box>
                )}
              </>
            ) : (
              /* Edit — locked design with image */
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
                {selectedDesign?.image_url ? (
                  <Box
                    component="img"
                    src={selectedDesign.image_url}
                    alt={editingRecord.designNo}
                    onClick={() => selectedDesign.image_url && setLightboxUrl(selectedDesign.image_url)}
                    sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 1.5, flexShrink: 0, cursor: 'zoom-in' }}
                  />
                ) : (
                  <Avatar variant="rounded" sx={{ width: 64, height: 64, bgcolor: 'grey.200' }}>
                    <BrokenImageIcon sx={{ color: 'grey.400' }} />
                  </Avatar>
                )}
                <Box>
                  <Typography variant="body2" fontWeight={700}>{editingRecord.designNo}</Typography>
                  {editingRecord.design?.name && <Typography variant="caption" color="text.secondary">{editingRecord.design.name}</Typography>}
                  <Typography variant="caption" color="text.disabled" display="block">Design No cannot be changed</Typography>
                </Box>
              </Box>
            )}

            {/* Dupatta Type */}
            <FormControl fullWidth>
              <InputLabel>Dupatta Type</InputLabel>
              <Select value={form.dupattaTypeId} onChange={(e) => setForm({ ...form, dupattaTypeId: e.target.value })} label="Dupatta Type">
                <MenuItem value=""><em>None</em></MenuItem>
                {dupattaTypes.map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
              </Select>
            </FormControl>

            {/* Description */}
            <TextField
              label="Description" fullWidth multiline rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Dupatta details, fabric info, etc."
            />

            {/* Quantity */}
            <TextField
              label="Quantity" fullWidth type="number"
              value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              inputProps={{ min: 0 }}
              helperText="Number of dupatta pieces planned"
            />

            {/* Dupatta Image upload */}
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>Dupatta Image</Typography>
              <input
                type="file"
                accept="image/*"
                ref={dupattaFileRef}
                style={{ display: 'none' }}
                onChange={handleDupattaImageSelect}
              />
              <Stack direction="row" spacing={1.5} alignItems="center">
                {/* Preview */}
                {form.dupattaImageUrl ? (
                  <Box sx={{ position: 'relative', flexShrink: 0 }}>
                    <Box
                      component="img"
                      src={form.dupattaImageUrl}
                      alt="Dupatta"
                      onClick={() => setLightboxUrl(form.dupattaImageUrl)}
                      sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1.5, cursor: 'zoom-in', border: '2px solid', borderColor: 'primary.light' }}
                    />
                    <Tooltip title="Enlarge">
                      <IconButton
                        size="small"
                        onClick={() => setLightboxUrl(form.dupattaImageUrl)}
                        sx={{ position: 'absolute', bottom: 2, right: 2, bgcolor: 'rgba(0,0,0,0.45)', color: 'white', p: '2px', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                      >
                        <ZoomInIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <Avatar variant="rounded" sx={{ width: 72, height: 72, bgcolor: 'grey.100' }}>
                    <BrokenImageIcon sx={{ color: 'grey.400' }} />
                  </Avatar>
                )}
                <Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={uploadingDupatta ? <CircularProgress size={14} /> : <UploadIcon />}
                    onClick={() => dupattaFileRef.current?.click()}
                    disabled={uploadingDupatta}
                  >
                    {uploadingDupatta ? 'Uploading…' : form.dupattaImageUrl ? 'Replace Image' : 'Upload Image'}
                  </Button>
                  {form.dupattaImageUrl && (
                    <Button
                      size="small"
                      color="error"
                      sx={{ ml: 1 }}
                      onClick={() => setForm((f) => ({ ...f, dupattaImageUrl: '' }))}
                    >
                      Remove
                    </Button>
                  )}
                  {uploadingDupatta && <LinearProgress sx={{ mt: 0.75, borderRadius: 1 }} />}
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    JPEG / PNG / WEBP, max 5 MB
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 1.5 } }}>
          <Button onClick={closeDialog} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.designNo.trim()}>
            {saving ? <CircularProgress size={20} /> : editingRecord ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DupattaTrackerPage;
